import { DateTime } from 'luxon'

import type { ShiftBandDto, ShiftBandsResponse } from '../ajax/ConfigAjax'
import { queryClient } from '../queryClient'
import type { DoubleShift, TripleShift } from '../types/Shift'

export const SHIFT_BANDS_QUERY_KEY = ['config', 'shiftBands'] as const

const FALLBACK_BANDS: ShiftBandDto[] = [
  { shift: 'D', shiftNumber: 1, startTime: '06:00', durationHours: 12 },
  { shift: 'A', shiftNumber: 2, startTime: '14:00', durationHours: 8 },
  { shift: 'N', shiftNumber: 3, startTime: '18:00', durationHours: 12 },
]

function bandLetter(band: ShiftBandDto): string {
  return (band.shift ?? '').trim().toUpperCase().charAt(0)
}

export function getShiftBands(): ShiftBandDto[] {
  const cached = queryClient.getQueryData<ShiftBandsResponse | null>(SHIFT_BANDS_QUERY_KEY)
  const bands = cached?.shiftBands?.length ? [...cached.shiftBands] : [...FALLBACK_BANDS]
  return bands.sort((a, b) => a.shiftNumber - b.shiftNumber)
}

export function nextShiftAfter(dateIso: string, shift: string): { date: string; shift: DoubleShift } {
  if (shift === 'NIGHT') {
    return { date: DateTime.fromISO(dateIso).plus({ days: 1 }).toISODate()!, shift: 'DAY' }
  }
  return { date: dateIso, shift: 'NIGHT' }
}

// Note: takes any shift name or letter. This was DoubleShift-only and mapped anything that was not DAY
// to the night band, so an afternoon shift got an 18:00 start and a twelve-hour length.
export function shiftStart(dateIso: string, shift: TripleShift | DoubleShift | string): DateTime {
  const hour = shiftStartHour(shift)
  const minute = Number((bandForLetter(shift)?.startTime ?? '').split(':')[1] ?? 0)
  return DateTime.fromISO(dateIso).startOf('day').set({ hour, minute: Number.isFinite(minute) ? minute : 0 })
}

export function shiftEnd(dateIso: string, shift: TripleShift | DoubleShift | string): DateTime {
  return shiftStart(dateIso, shift).plus({ hours: shiftDurationHours(shift) })
}

export function shiftStartHour(shift: string): number {
  const band = bandForLetter(shift)
  const configured = band?.startTime ? Number(band.startTime.split(':')[0]) : NaN
  if (Number.isFinite(configured)) return configured
  const letter = shiftLetter(shift)
  return letter === 'N' ? 18 : letter === 'A' ? 14 : 6
}

// Note: how many hourly buckets a shift has. The production tables assumed 12, which is right for day
// and night here but wrong for the configured 8-hour afternoon band, and wrong for any site that runs
// something else - it would offer hour buttons past the end of the shift.
export function shiftDurationHours(shift: string): number {
  const configured = bandForLetter(shift)?.durationHours
  if (Number.isFinite(configured) && (configured as number) > 0) return configured as number
  return shiftLetter(shift) === 'A' ? 8 : 12
}

function shiftLetter(shift: string): string {
  return (shift || '').trim().toUpperCase().charAt(0)
}

function bandForLetter(shift: string): ShiftBandDto | undefined {
  return getShiftBands().find(b => bandLetter(b) === shiftLetter(shift))
}

export function reportDateOf(time: DateTime): string {
  const day = time.startOf('day')
  for (const date of [day.minus({ days: 1 }), day]) {
    const iso = date.toISODate()!
    if (getShiftBands().some(band => shiftStart(iso, band.shift) <= time && time < shiftEnd(iso, band.shift))) return iso
  }
  return day.toISODate()!
}
